import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export type AppLocale = 'de' | 'en'

const STORAGE_KEY = 'revyn.locale'

/**
 * Read the user's preferred UI locale. Order:
 *   1. localStorage override
 *   2. <html lang="..."> attribute
 *   3. browser language prefix
 *   4. 'de' fallback
 */
export function detectInitialLocale(): AppLocale {
  if (typeof window === 'undefined') return 'de'

  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'de' || stored === 'en') return stored

  const htmlLang = document.documentElement.lang?.toLowerCase()
  if (htmlLang?.startsWith('en')) return 'en'
  if (htmlLang?.startsWith('de')) return 'de'

  const browserLang = navigator.language?.toLowerCase() ?? ''
  if (browserLang.startsWith('en')) return 'en'

  return 'de'
}

export function useLocale() {
  const i18n = useI18n()

  const current = computed<AppLocale>({
    get: () => (i18n.locale.value as AppLocale),
    set: (value) => setLocale(value),
  })

  function setLocale(value: AppLocale) {
    i18n.locale.value = value
    document.documentElement.lang = value
    try {
      localStorage.setItem(STORAGE_KEY, value)
    } catch {
      // ignore storage errors (e.g. private mode)
    }
  }

  const available: { code: AppLocale; label: string }[] = [
    { code: 'de', label: 'DE' },
    { code: 'en', label: 'EN' },
  ]

  return { current, setLocale, available }
}
